import { SanitizeHTMLString } from "@circle-react/components/shared/uikit/SanitizeContent";
import { Modal as CompassModal } from "@circleco/compass/components/Modal";
import type { ChangeEvent } from "react";

import { t } from "@/i18n-js/instance";
import { Input } from "@/react/components/SettingsApp/CompassCompat/Input";
import type { SecureConfirmationModalProps } from "@/react/components/SettingsApp/CompassCompat/SecureConfirmationModal";

import { useSecureConfirmationModal } from "../useSecureConfirmationModal";

const i18nRoot = "modals.secure_confirmation";

export const CompassSecureConfirmationModal = ({
  title,
  description,
  cancelText = t([i18nRoot, "cancel"]),
  confirmText = t([i18nRoot, "confirm"]),
  confirmUserPrompt = t([i18nRoot, "confirm_prompt"]),
  confirmVariant = "primary",
  isDisabled,
  isOpen,
  onClose,
  onConfirm,
  onCancel,
  position,
  promptGrouping = "default",
  size = "md",
  children,
}: SecureConfirmationModalProps) => {
  const {
    userInput,
    setUserInput,
    shouldDisableButton,
    compassVariant,
    handleOpenChange,
    handleCancel,
    handleConfirm,
  } = useSecureConfirmationModal({
    confirmUserPrompt,
    confirmVariant,
    isDisabled,
    onCancel,
    onClose,
    onConfirm,
  });

  const promptDescription = (
    <SanitizeHTMLString
      content={t([i18nRoot, "prompt_description"], {
        prompt: confirmUserPrompt,
      })}
    />
  );

  const confirmInput = (
    <Input
      type="text"
      value={userInput}
      onChange={(e: ChangeEvent<HTMLInputElement>) =>
        setUserInput(e.target.value)
      }
      data-testid="secure-confirmation-input"
    />
  );

  return (
    <CompassModal.Root
      open={isOpen}
      onOpenChange={handleOpenChange}
      size={size}
      position={position}
    >
      <CompassModal.Content>
        <CompassModal.Header
          title={title}
          description={description}
          showCloseButton
        />
        <CompassModal.Body>
          {promptGrouping === "inline" ? (
            <div className="flex flex-col gap-4">
              {children}
              <div className="flex flex-col gap-1.5">
                {promptDescription}
                {confirmInput}
              </div>
            </div>
          ) : (
            <div className="flex flex-col gap-4">
              <div className="flex flex-col gap-6">
                <div>{children}</div>
                {promptDescription}
              </div>
              {confirmInput}
            </div>
          )}
        </CompassModal.Body>
        <CompassModal.Footer
          secondaryAction={{
            label: cancelText,
            onClick: handleCancel,
            shouldCloseModal: false,
          }}
          primaryAction={{
            label: confirmText,
            variant: compassVariant,
            disabled: shouldDisableButton,
            onClick: handleConfirm,
            shouldCloseModal: false,
          }}
        />
      </CompassModal.Content>
    </CompassModal.Root>
  );
};
